import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Skeleton,
  Stack,
} from '@mui/material';
import { useAuth, usePermissions } from '../contexts/AuthContext';
import { hasPermission, P } from '../utils/permissions';
import { getTimeOfDayGreeting } from './dashboard/utils/greeting';
import { displaySx } from '../theme/typography';
import { useDashboardSummary } from './dashboard-widgets';
import { useConflictsSummary, conflictsRangeHasEnded } from '../hooks/useConflictsSummary';
import { useDashboardData } from './dashboard/hooks/useDashboardData';
import { useSectionFilters } from './dashboard/hooks/useSectionFilters';
import {
  MyTimeSection,
  FinancialsSection,
  BillingSection,
  ProjectsSection,
  AllocationsSection,
  TeamSection,
  AvailabilitySection,
  SectionFilters,
  DashboardAlerts,
} from './dashboard/components';
import {
  RevenueTileConfigDialog,
  PipelineValueTileConfigDialog,
  ActiveProjectsConfigDialog,
} from './dashboard/dialogs';
import { DateRangeControl } from './shared/dateRange';

const Dashboard: React.FC = React.memo(() => {
  const { user } = useAuth();
  const { permissions } = usePermissions();
  const navigate = useNavigate();

  const {
    loading,
    error,
    lastUpdated,
    dateRangeFilter,
    handleDateRangeChange,
    dashboardConfig,
    availableStages,
    saveDashboardConfig,
    reloadDashboardConfig,
    consultants,
    revenueSummary,
    isConsultantRole,
  } = useDashboardData();

  // KpiStrip data comes from /api/dashboard/summary — fetched once at the
  // shell and threaded into the strip + the action-widget sections.
  const {
    data: summary,
    loading: summaryLoading,
    error: summaryError,
  } = useDashboardSummary();

  // Shared conflicts summary (item 4, AC-19) — the same server-side
  // computation the Planning Board Conflicts tab lists, so the alert count
  // always matches the tab for the selected range. Skipped for users without
  // the alert's UI gate (AC-R1); an error suppresses the alert (the strip is
  // auxiliary, never a page failure) but is logged so a silent zero is
  // distinguishable from a healthy one.
  const { summary: conflictsSummary, error: conflictsError } = useConflictsSummary(
    dateRangeFilter.startDate,
    dateRangeFilter.endDate,
    {
      // Two reasons to issue nothing. The permission gate is the alert's (AC-R1);
      // the wholly-past skip is shared with the Reports card so the two surfaces
      // produce the same traffic for the same range — the endpoint would return
      // an empty summary for it either way now that ended windows are dropped.
      skip:
        !hasPermission(permissions, P.VIEW_REPORTS_OPERATIONAL) ||
        conflictsRangeHasEnded(dateRangeFilter.endDate),
    },
  );
  useEffect(() => {
    if (conflictsError) {
      console.error('Failed to load allocation conflicts for the dashboard alert:', conflictsError);
    }
  }, [conflictsError]);

  // Section gating + persisted filter chips (SPEC §Outcomes 2 and 3).
  // Render rule is **gate AND filter**: a section is visible iff its
  // permission gate passes AND it is in the user's visible set. The
  // absent-not-masked rule means a gated-out section appears nowhere
  // (neither chip nor body) and a stale localStorage entry cannot
  // resurrect it (the hook clamps the persisted set on every read).
  const { sections, visibleSet, isVisible, toggle, showAll, showChipRow } =
    useSectionFilters(permissions);

  // Dialog open/close is pure UI state, kept in the shell. The date-range
  // dialog's open state now lives inside the shared `DateRangeControl`.
  const [revenueTileConfigOpen, setRevenueTileConfigOpen] = useState(false);
  const [pipelineTileConfigOpen, setPipelineTileConfigOpen] = useState(false);
  const [activeProjectsTileConfigOpen, setActiveProjectsTileConfigOpen] = useState(false);

  const onNavigateToTimesheets = (options?: {
    tab?: 'timesheets' | 'my-timesheet' | 'reports' | 'settings';
    weekISO?: string;
    monthISO?: string;
    employeeId?: string;
  }) => {
    const params = new URLSearchParams();
    if (options?.tab && options.tab !== 'my-timesheet') {
      params.set('tab', options.tab);
    }
    if (options?.monthISO) {
      // Monthly-cadence deep-link → the month view at that month (AC-D5).
      params.set('view', 'monthly');
      params.set('month', options.monthISO);
    } else if (options?.weekISO) {
      params.set('week', options.weekISO);
    }
    if (options?.employeeId) {
      // Pairs with the URL hydration effect in Timesheets.tsx so the
      // admin review grid opens directly on the right person + week.
      params.set('employeeId', options.employeeId);
    }
    const qs = params.toString();
    navigate(qs ? `/timesheets?${qs}` : '/timesheets');
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width={200} height={40} />
          <Skeleton variant="text" width={400} height={20} />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Box key={i} sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' } }}>
              <Card>
                <CardContent>
                  <Skeleton variant="circular" width={40} height={40} />
                  <Skeleton variant="text" height={20} sx={{ mt: 1 }} />
                  <Skeleton variant="text" height={30} />
                </CardContent>
              </Card>
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header — mirrors the prototype's PageTitle (eyebrow + h1 +
          sub). The h1 uses the v2 `displaySx` helper (Geist 500 / -0.025em)
          and the default ink colour; the v1 blue gradient is gone. The
          Refresh button was removed in Phase 3b (auto-fetch + TTL cache
          invalidation already keeps the dashboard fresh on mount). */}
      <Box sx={{ mb: 4, minWidth: 0 }}>
        <Typography
          component="h1"
          sx={[
            displaySx,
            {
              fontSize: '1.625rem',
              color: 'text.primary',
              lineHeight: 1.15,
              mb: 0.5,
            },
          ]}
        >
          {getTimeOfDayGreeting()}{user?.firstName ? `, ${user.firstName}` : ''}.
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Here's your business overview for {dateRangeFilter.label}. Click the date range to change the time period.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, flexWrap: 'wrap' }}>
          <DateRangeControl
            value={dateRangeFilter}
            onChange={handleDateRangeChange}
          />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert
          severity="error"
          variant="outlined"
          sx={(theme) => ({
            mb: 3,
            borderRadius: 1,
            borderLeftWidth: 3,
            backgroundColor: theme.palette.statusSoft.danger,
          })}
        >
          {error}
        </Alert>
      )}

      {summaryError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {summaryError}
        </Alert>
      )}

      {showChipRow && (
        <SectionFilters
          sections={sections}
          visibleSet={visibleSet}
          onToggle={toggle}
          onShowAll={showAll}
        />
      )}

      {/* Alerts always live above all sections (Phase 3b) so urgent info
          is visible regardless of which section chips the user has on.
          Each alert is permission-gated internally. Resolve deep-links to
          the Planning Board Conflicts tab (AC-23), which lists exactly the
          consultants this alert counts (AC-19). */}
      <DashboardAlerts
        conflictConsultants={conflictsSummary?.consultants ?? []}
        atRiskProjects={(revenueSummary?.atRisk ?? []).filter((p) => p.flags.includes('ending-soon'))}
        onNavigateToPlanning={() => navigate('/planning?tab=conflicts')}
        onNavigateToProjects={() => navigate('/projects')}
      />

      {/* Sections render only when gate(perms) passes AND the user has
          left the chip toggled on. The registry in `dashboard/sections.tsx`
          owns gate ↔ id ↔ label ↔ icon; this shell just maps id → JSX.
          Phase 3b: Financials now owns its 4 KPI tiles + 2 chart panels
          (KpiStrip retired); chip toggle finally hides them all. */}
      <Stack spacing={3}>
        {isVisible('financials') && (
          <FinancialsSection
            summary={revenueSummary}
            revenueCalculationMethod={dashboardConfig.revenueCalculationMethod}
            dateRangeLabel={dateRangeFilter.label}
            revenueByMonth={summary?.widgets?.revenueByMonth}
            loading={loading || summaryLoading}
            onConfigureActiveProjects={() => setActiveProjectsTileConfigOpen(true)}
            onConfigureRevenue={() => setRevenueTileConfigOpen(true)}
            onConfigurePipeline={() => setPipelineTileConfigOpen(true)}
            onOpenReports={() => navigate('/reports')}
            onViewAllProjects={() => navigate('/projects')}
          />
        )}
        {isVisible('billing') && (
          <BillingSection
            billing={summary?.widgets?.billing}
            loading={summaryLoading}
            onOpenReports={() => navigate('/reports?tab=billing')}
          />
        )}
        {isVisible('mytime') && (
          <MyTimeSection
            myWeek={summary?.widgets?.myWeek}
            loading={summaryLoading}
            onNavigateToTimesheets={onNavigateToTimesheets}
          />
        )}
        {isVisible('projects') && (
          <ProjectsSection
            widgets={summary?.widgets}
            loading={summaryLoading}
            onNavigateToProjects={() => navigate('/projects')}
            onNavigateToPlanning={() => navigate('/planning')}
            onNavigateToProposedAllocations={() => navigate('/planning?tab=proposed')}
          />
        )}
        {isVisible('allocations') && (
          <AllocationsSection
            allocations={summary?.widgets?.myAllocations}
            loading={summaryLoading}
            onOpenProject={(projectId) => navigate(`/projects/${projectId}`)}
          />
        )}
        {isVisible('team') && (
          <TeamSection
            loading={summaryLoading}
            consultants={consultants}
            isConsultantRole={isConsultantRole}
            teamStatusToday={summary?.widgets?.teamStatusToday}
            onViewAllPlanning={() => navigate('/planning')}
          />
        )}
        {isVisible('availability') && (
          <AvailabilitySection
            myAbsences={summary?.widgets?.myAbsences}
            teamAbsencesThisWeek={summary?.widgets?.teamAbsencesThisWeek}
            awayScope={summary?.widgets?.awayScope}
            loading={summaryLoading}
            onSeeAllTeamAbsences={() => navigate('/team-absences')}
          />
        )}
      </Stack>

      <RevenueTileConfigDialog
        open={revenueTileConfigOpen}
        onClose={() => setRevenueTileConfigOpen(false)}
        dashboardConfig={dashboardConfig}
        onSave={saveDashboardConfig}
        reloadConfig={reloadDashboardConfig}
      />
      <PipelineValueTileConfigDialog
        open={pipelineTileConfigOpen}
        onClose={() => setPipelineTileConfigOpen(false)}
        dashboardConfig={dashboardConfig}
        availableStages={availableStages}
        onSave={saveDashboardConfig}
        reloadConfig={reloadDashboardConfig}
      />
      <ActiveProjectsConfigDialog
        open={activeProjectsTileConfigOpen}
        onClose={() => setActiveProjectsTileConfigOpen(false)}
        dashboardConfig={dashboardConfig}
        availableStages={availableStages}
        onSave={saveDashboardConfig}
        reloadConfig={reloadDashboardConfig}
      />
    </Box>
  );
});

Dashboard.displayName = 'Dashboard';

export default Dashboard;
